import React, { FC, useEffect, useState } from "react"

import { isClientSide } from "@utils"
import { DELIGHT_LABS_IMAGE_IDS, DELIGHT_LABS_IMAGE_URLS } from "@constants"

import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import Modal from "@components/Modal/Modal"

import "./DelightLabsShareModal.scss"

export type DELIGHT_LABS_SHARE_DATA = {
  imageId: DELIGHT_LABS_IMAGE_IDS,
  name: string,
  shareText: string,
}

const LS_AUTH_KEY = `hs--linkedin_auth`
export const LS_IMAGE_KEY = `hs--delightlabs_imageId`

const DelightLabsShareModal: FC<{
  currentModalData: DELIGHT_LABS_SHARE_DATA | null,
  onClose: () => void,
}> = ({
  currentModalData,
  onClose,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
    const [authData, setAuthData] = useState<any>({})
    const [isSharing, setIsSharing] = useState<boolean>(false)
    const [isShared, setIsShared] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)

    const [text, setText] = useState<string>("")

    const handleClickAuth = () => {
      if (currentModalData?.imageId) window.localStorage.setItem(LS_IMAGE_KEY, currentModalData.imageId)
    }

    // Get LinkedIn auth data, if it exists
    useEffect(() => {
      if (!isClientSide()) return

      const lsAuthData = JSON.parse(localStorage.getItem(LS_AUTH_KEY) || "{}")

      if (lsAuthData.access_token) {
        setIsAuthenticated(true)
        setAuthData(lsAuthData)
        setIsLoading(false)
        return
      }

      const searchParams = new URLSearchParams(window.location.search.replace("?", ""))

      const searchParamsAuthData = JSON.parse(searchParams.get("linkedin_auth") || "{}")

      if (searchParamsAuthData.access_token) {
        localStorage.setItem(LS_AUTH_KEY, JSON.stringify(searchParamsAuthData))
        setIsAuthenticated(true)
        setAuthData(searchParamsAuthData)
        window.history.replaceState({}, document.title, window.location.pathname)
      }

      setIsLoading(false)
    }, [])

    useEffect(() => {
      if (!isClientSide()) return

      if (!currentModalData) {
        setText("")
        setIsLoading(false)
        setIsShared(false)
        return
      }

      setText(currentModalData.shareText)
    }, [currentModalData])

    const handleClickShare = () => {
      if (!currentModalData) return

      setIsSharing(true)

      try {
        const accessToken = authData.access_token
        const userId = authData.user_id
        const expiresAt = authData.expires_at

        if (!accessToken || !userId || Date.now() >= expiresAt) throw new Error("Not authenticated. Please log in first.")

        fetch("/api/linkedin/share", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            accessToken,
            text,
            imageId: currentModalData.imageId,
            userId,
          }),
        })
          .then((response) => {
            setIsSharing(false)
            if (response.ok) {
              setIsShared(true)
            } else {
              throw new Error("Failed to share post")
            }
          })
      } catch (error) {
        console.error("Error sharing post:", error)
        setIsSharing(false)
        setIsError(true)
      }
    }

    return currentModalData ? (
      <Modal
        onClose={onClose}
        useInsetCloseButton
        width="min(823px, var(--container-width))"
      >
        <div className="DelightLabsShareModal">
          <h3>Help this experiment escape the lab!</h3>
          <p className="type--text-L margin-bottom--M">Connect your LinkedIn, mention Help Scout in your post, and share to enter this week's swag giveaway.</p>
          {isLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              {!isAuthenticated && (
                <a href="/api/linkedin/auth" onClick={handleClickAuth} className="button button--L">
                  <Icon
                    color="var(--color--basics-white)"
                    type={ICON_TYPES["linkedin"]}
                    size="1.4em"
                  />
                  <span>Connect LinkedIn</span>
                </a>
              )}
              {isAuthenticated && !isShared && (
                <div className="linkedin-post-editor">
                  <label>
                    <span className="type--text-XS">Post text</span>
                    <textarea
                      disabled={isSharing}
                      onChange={(e) => setText(e.target.value)}
                      value={text}
                    />
                  </label>
                  <label>
                    <span className="type--text-XS">Image preview</span>
                    <img src={DELIGHT_LABS_IMAGE_URLS[currentModalData.imageId]} />
                  </label>
                  <button
                    disabled={isSharing}
                    onClick={handleClickShare} className="button button--L"
                  >
                    <Icon
                      color="var(--color--basics-white)"
                      type={ICON_TYPES[isSharing ? "loading" : "linkedin"]}
                      size="1.4em"
                    />
                    {isSharing ? "Sharing..." : "Post to LinkedIn"}
                  </button>
                  <p className="type--text-S margin-bottom--0">Clicking this button will post directly to LinkedIn. It’s like magic.</p>
                </div>
              )}
              {isAuthenticated && isShared && (
                <p className="success font-size--L">
                  🎉🎉🎉
                  {" "}
                  Thanks for sharing! You've been entered in our giveaway.
                  {" "}
                  🎉🎉🎉
                </p>
              )}
              {isAuthenticated && isError && (
                <p className="font-size--L color--error-text">
                  <Icon
                    type={ICON_TYPES["alert-error"]}
                    color="var(--color--error-icon)"
                  />
                  {" "}
                  Sorry, something went wrong.
                </p>
              )}
            </>
          )}
        </div>
      </Modal >
    ) : null
  }

export default DelightLabsShareModal
